import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import {
  ROTULO_DA_MEXIDA,
  alterarProduto,
  brl,
  buscarNoCatalogo,
  diaCurto,
  horaBrasilia,
  incluirProduto,
  lerHistorico,
  lerPainel,
  lerProdutos,
  lerVendas,
  nomeBonito,
  qtd,
  sinal,
  textoDaSituacao,
} from '../../geladeira';
import type {
  ConferenciaDaGeladeira,
  HistoricoDaGeladeira,
  LinhaDoPainel,
  MexidaDaGeladeira,
  PainelDaGeladeira,
  ProdutoDaGeladeira,
  ProdutoDoCatalogo,
  VendaDaGeladeira,
  VendasDaGeladeira,
} from '../../geladeira';
import '../central/central.css';
import './geladeira.css';

/**
 * GELADEIRA — o painel do dono, do Carlos e dos admins (19/10/2026).
 *
 *   Hoje       contado (farmácia + estoque) × HOS na contagem × vendeu × entrou × mexeu × HOS agora
 *   Vendas     cada venda: quando, quem vendeu, para quem, quanto, como pagou e em quantas vezes
 *   Histórico  as conferências (com as recontagens) e as mexidas que não foram venda
 *   Produtos   a lista da conferência (pôr, tirar, renomear, ordenar)
 *
 * Tudo o que aparece aqui também vai ao robô @inova_geladeira_bot na hora em que acontece.
 */

type Aba = 'hoje' | 'vendas' | 'historico' | 'produtos';
const ABAS: readonly { id: Aba; rotulo: string; icone: NomeDoIcone }[] = [
  { id: 'hoje', rotulo: 'Hoje', icone: 'gelo' },
  { id: 'vendas', rotulo: 'Vendas', icone: 'receipt' },
  { id: 'historico', rotulo: 'Histórico', icone: 'clock' },
  { id: 'produtos', rotulo: 'Produtos', icone: 'box' },
];

const hojeBrasilia = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const menosDias = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);

function irPara(p: Record<string, string | null>) {
  const atual = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  for (const [k, v] of Object.entries(p)) {
    if (v === null) atual.delete(k);
    else atual.set(k, v);
  }
  const s = atual.toString();
  window.location.hash = `#/geladeira${s ? `?${s}` : ''}`;
}

export function PainelGeladeira({
  sessao,
  parametros,
  podeAdministrar,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
  podeAdministrar: boolean;
}) {
  const aba = ABAS.find((a) => a.id === parametros.get('aba'))?.id ?? 'hoje';
  return (
    <section className="gd gd-painel">
      <header className="gd-cabeca gd-cabeca-painel">
        <span className="gd-selo" aria-hidden="true">
          <Icone nome="gelo" tamanho={22} traco={1.6} />
        </span>
        <div>
          <h1>Geladeira</h1>
          <p className="gd-sub">
            Canetas e produtos de alto valor: a conferência do Iggor, o HOS e cada venda.
          </p>
        </div>
        <a className="gd-btn gd-btn-sec gd-btn-curto" href="#/geladeira-conferir">
          <Icone nome="check" tamanho={15} traco={2} />
          Conferir agora
        </a>
      </header>
      <nav className="gd-abas" aria-label="Geladeira">
        {ABAS.filter((a) => a.id !== 'produtos' || podeAdministrar).map((a) => (
          <a
            key={a.id}
            href={`#/geladeira${a.id === 'hoje' ? '' : `?aba=${a.id}`}`}
            className={a.id === aba ? 'gd-aba gd-aba-ativa' : 'gd-aba'}
            aria-current={a.id === aba ? 'page' : undefined}
          >
            <Icone nome={a.icone} tamanho={15} />
            {a.rotulo}
          </a>
        ))}
      </nav>
      {aba === 'hoje' && <AbaHoje dia={parametros.get('dia')} />}
      {aba === 'vendas' && <AbaVendas parametros={parametros} />}
      {aba === 'historico' && <AbaHistorico parametros={parametros} />}
      {aba === 'produtos' && podeAdministrar && <AbaProdutos sessao={sessao} />}
    </section>
  );
}

/* ================================================================== *
 * HOJE
 * ================================================================== */

function AbaHoje({ dia }: { dia: string | null }) {
  const [p, setP] = useState<PainelDaGeladeira | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerPainel(dia).then((r) => (r.ok ? setP(r.dados) : setErro(true)));
  }, [dia]);
  useEffect(() => {
    setP(null);
    carregar();
    // o painel de HOJE se atualiza sozinho a cada minuto (o vigia também roda de minuto em minuto)
    if (dia && dia !== hojeBrasilia()) return;
    const t = setInterval(carregar, 60_000);
    return () => clearInterval(t);
  }, [carregar, dia]);

  if (erro) return <ErroDeArea oQue="o painel da geladeira" aoTentar={carregar} />;
  const diaAtual = p?.dia ?? dia ?? hojeBrasilia();
  const c = p?.conferencia ?? null;

  return (
    <div className="gd-corpo">
      <div className="gd-barra-dia">
        <button
          type="button"
          className="gd-btn-icone"
          onClick={() => irPara({ dia: menosDias(diaAtual, 1) })}
          aria-label="Dia anterior"
        >
          <Icone nome="arrow-l" tamanho={16} />
        </button>
        <input
          type="date"
          value={diaAtual}
          max={hojeBrasilia()}
          onChange={(e) =>
            e.target.value &&
            irPara({ dia: e.target.value === hojeBrasilia() ? null : e.target.value })
          }
          aria-label="Dia"
        />
        <button
          type="button"
          className="gd-btn-icone"
          disabled={diaAtual >= hojeBrasilia()}
          onClick={() => {
            const prox = menosDias(diaAtual, -1);
            irPara({ dia: prox >= hojeBrasilia() ? null : prox });
          }}
          aria-label="Próximo dia"
        >
          <Icone nome="arrow" tamanho={16} />
        </button>
        <span className="gd-barra-dia-txt">
          {p?.hoje ? 'Hoje' : diaCurto(diaAtual)}
          {p?.hoje && p.hosLidoEm && (
            <> · HOS lido às {horaBrasilia(p.hosLidoEm)} · atualiza a cada minuto</>
          )}
        </span>
      </div>

      <div className="gd-kpis">
        <Cartao
          rotulo="Conferência"
          icone="check"
          valor={
            p === null
              ? null
              : c
                ? c.itensDivergentes
                  ? `${String(c.itensDivergentes)} não bateram`
                  : 'Tudo bateu'
                : 'Não conferida'
          }
          sub={
            c
              ? `${nomeBonito(c.por.nome) ?? c.por.nome} às ${horaBrasilia(c.fechadaEm)}`
              : p?.hoje
                ? 'o Iggor ainda não fechou hoje'
                : 'ninguém conferiu neste dia'
          }
          tom={c ? (c.itensDivergentes ? 'erro' : 'ok') : 'aviso'}
        />
        <Cartao
          rotulo="Vendidas no dia"
          icone="receipt"
          valor={p === null ? null : `${qtd(p.totais.vendidas)} un.`}
          sub={p ? brl(p.totais.valorVendido) : null}
        />
        <Cartao
          rotulo="Mexeu sem venda"
          icone="warn"
          valor={p === null ? null : String(p.totais.mexidasSemVenda)}
          sub={
            p
              ? p.totais.mexidasSemVenda
                ? 'perda, ajuste, consumo… — veja abaixo'
                : 'nenhuma mexida suspeita'
              : null
          }
          tom={p && p.totais.mexidasSemVenda ? 'erro' : undefined}
        />
        <Cartao
          rotulo="No HOS agora"
          icone="gelo"
          valor={
            p === null
              ? null
              : p.totais.unidadesNoHos === null
                ? '—'
                : `${qtd(p.totais.unidadesNoHos)} un.`
          }
          sub={p ? `${String(p.totais.produtos)} produtos na lista` : null}
        />
      </div>

      {p && !p.linhas.length ? (
        <EstadoVazio
          icone="gelo"
          titulo="A lista está vazia"
          texto="Escolha os produtos na aba Produtos."
        />
      ) : (
        <div className="gd-bloco">
          <table className="cv-tabela gd-tabela">
            <thead>
              <tr>
                <th>Produto</th>
                <th className="cv-dir">Farmácia</th>
                <th className="cv-dir">Estoque</th>
                <th className="cv-dir">Contado</th>
                <th className="cv-dir">HOS na contagem</th>
                <th>Situação</th>
                <th className="cv-dir">Vendeu</th>
                <th className="cv-dir">Entrou</th>
                <th className="cv-dir">Mexeu</th>
                <th className="cv-dir" title="Contado + tudo o que o HOS lançou depois da contagem">
                  Deveria ter
                </th>
                <th className="cv-dir">HOS agora</th>
              </tr>
            </thead>
            <tbody>
              {p === null
                ? Array.from({ length: 6 }, (_, i) => (
                    <tr key={i} className="gd-esqueleto">
                      <td colSpan={11}>
                        <span />
                      </td>
                    </tr>
                  ))
                : p.linhas.map((l) => <LinhaHoje key={l.produtoId} l={l} />)}
            </tbody>
          </table>
          <p className="gd-nota">
            <b>Deveria ter</b> = contado + o que o HOS lançou depois da contagem (venda tira, nota
            põe). Se o HOS agora for diferente disso, alguém mexeu sem registro — o robô já avisou.
          </p>
        </div>
      )}

      {p && p.mexidas.length > 0 && (
        <div className="gd-bloco">
          <h2 className="gd-h2">Mexidas no dia que não foram venda</h2>
          <ListaDeMexidas
            mexidas={p.mexidas}
            nomes={new Map(p.linhas.map((l) => [l.codigoHos, l.nome]))}
          />
        </div>
      )}
      {c?.observacao && (
        <p className="gd-obs-lida">
          <Icone nome="edit" tamanho={14} /> Observação do {nomeBonito(c.por.nome) ?? c.por.nome}:{' '}
          {c.observacao}
        </p>
      )}
    </div>
  );
}

function LinhaHoje({ l }: { l: LinhaDoPainel }) {
  const k = l.contagem;
  const tom =
    l.situacao === 'BATEU'
      ? 'ok'
      : l.situacao === 'SOBRA' || l.situacao === 'FALTA'
        ? 'erro'
        : l.situacao === 'HOS_SEM_LEITURA'
          ? 'cinza'
          : 'aviso';
  const divergeAgora =
    l.deveriaTer !== null && l.hosAgora !== null && Math.abs(l.deveriaTer - l.hosAgora) > 1e-6;
  return (
    <tr className={tom === 'erro' ? 'gd-linha-erro' : undefined}>
      <td>
        <strong className="gd-nome">{l.nome}</strong>
        {l.descricaoHos && <small className="gd-desc">{l.descricaoHos}</small>}
      </td>
      <td className="cv-dir gd-num">{k ? qtd(k.farmacia) : '—'}</td>
      <td className="cv-dir gd-num">{k ? qtd(k.estoque) : '—'}</td>
      <td className="cv-dir gd-num gd-forte">{k ? qtd(k.total) : '—'}</td>
      <td className="cv-dir gd-num">{k?.hosNaContagem == null ? '—' : qtd(k.hosNaContagem)}</td>
      <td>
        <span className={`gd-chip gd-chip-${tom}`}>{textoDaSituacao(l)}</span>
      </td>
      <td className="cv-dir gd-num">{l.vendeu ? qtd(l.vendeu) : '·'}</td>
      <td className="cv-dir gd-num">{l.entrou ? sinal(l.entrou) : '·'}</td>
      <td className={`cv-dir gd-num${l.mexidasSemVenda ? ' gd-txt-erro' : ''}`}>
        {l.mexidasSemVenda ? sinal(l.mexeuSemVenda) : '·'}
      </td>
      <td className="cv-dir gd-num">{l.deveriaTer === null ? '—' : qtd(l.deveriaTer)}</td>
      <td
        className={`cv-dir gd-num gd-forte${divergeAgora ? ' gd-txt-erro' : ''}`}
        title={divergeAgora ? 'Diferente do que deveria ter' : undefined}
      >
        {l.hosAgora === null ? '—' : qtd(l.hosAgora)}
      </td>
    </tr>
  );
}

function Cartao({
  rotulo,
  icone,
  valor,
  sub,
  tom,
}: {
  rotulo: string;
  icone: NomeDoIcone;
  valor: string | null;
  sub: string | null;
  tom?: 'ok' | 'erro' | 'aviso' | undefined;
}) {
  return (
    <div className={`gd-kpi${tom ? ` gd-kpi-${tom}` : ''}`}>
      <div className="gd-kpi-rotulo">
        <span>{rotulo}</span>
        <Icone nome={icone} tamanho={15} traco={1.5} />
      </div>
      {valor === null ? (
        <div className="gd-kpi-carregando" aria-busy="true" />
      ) : (
        <div className="gd-kpi-valor">{valor}</div>
      )}
      {sub && <div className="gd-kpi-sub">{sub}</div>}
    </div>
  );
}

function ListaDeMexidas({
  mexidas,
  nomes,
}: {
  mexidas: readonly MexidaDaGeladeira[];
  nomes: ReadonlyMap<string, string>;
}) {
  return (
    <ul className="gd-mexidas">
      {mexidas.map((m) => (
        <li key={m.idHos} className={m.classe === 'suspeito' ? 'gd-mexida-suspeita' : undefined}>
          <span className="gd-mexida-icone" aria-hidden="true">
            <Icone
              nome={m.classe === 'suspeito' ? 'warn' : m.delta > 0 ? 'down' : 'up-t'}
              tamanho={15}
            />
          </span>
          <div>
            <strong>
              {nomes.get(m.codigoHos) ?? m.codigoHos}{' '}
              <span className="gd-num">{sinal(m.delta)}</span>
            </strong>
            <small>
              {ROTULO_DA_MEXIDA[m.tipo] ?? m.tipo}
              {m.subtipo ? ` (${m.subtipo.replace(/_/g, ' ')})` : ''} · {diaCurto(m.dia)}{' '}
              {m.hora?.slice(0, 5) ?? ''}
              {m.documento ? ` · doc. ${String(m.documento)}` : ''}
              {m.fornecedor ? ` · ${m.fornecedor}` : ''}
            </small>
          </div>
          <span className={`gd-chip gd-chip-${m.classe === 'suspeito' ? 'erro' : 'cinza'}`}>
            {m.classe === 'suspeito' ? 'Sem venda' : 'Com documento'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ================================================================== *
 * VENDAS
 * ================================================================== */

type Janela = 'hoje' | '7' | '30';
const JANELAS: readonly { id: Janela; rotulo: string }[] = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: '7', rotulo: '7 dias' },
  { id: '30', rotulo: '30 dias' },
];

function AbaVendas({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const janela =
    JANELAS.find((j) => j.id === parametros.get('p'))?.id ?? (parametros.get('de') ? null : '7');
  const ate = janela ? hoje : (parametros.get('ate') ?? hoje);
  const de =
    janela === 'hoje'
      ? hoje
      : janela === '30'
        ? menosDias(hoje, 29)
        : janela === '7'
          ? menosDias(hoje, 6)
          : (parametros.get('de') ?? menosDias(hoje, 6));
  const produto = parametros.get('produto');
  const vendedor = parametros.get('vendedor');
  const [v, setV] = useState<VendasDaGeladeira | null>(null);
  const [erro, setErro] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const carregar = useCallback(() => {
    setErro(false);
    setV(null);
    void lerVendas({ de, ate, produto, vendedor }).then((r) =>
      r.ok ? setV(r.dados) : setErro(true),
    );
  }, [de, ate, produto, vendedor]);
  useEffect(carregar, [carregar]);

  const produtosDoFiltro = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of v?.vendas ?? []) m.set(x.codigoHos, x.nome ?? x.codigoHos);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [v]);

  const maisVendida = v
    ? [...v.resumo.porProduto].sort((x, y) => y.quantidade - x.quantidade || y.valor - x.valor)[0]
    : undefined;

  return (
    <div className="gd-corpo">
      <div className="gd-filtros">
        <div className="gd-segmentos" role="tablist" aria-label="Período">
          {JANELAS.map((j) => (
            <button
              key={j.id}
              type="button"
              role="tab"
              aria-selected={janela === j.id}
              className={janela === j.id ? 'gd-seg gd-seg-ativo' : 'gd-seg'}
              onClick={() => irPara({ p: j.id, de: null, ate: null })}
            >
              {j.rotulo}
            </button>
          ))}
        </div>
        <label className="gd-filtro-data">
          <span>De</span>
          <input
            type="date"
            value={de}
            max={ate}
            onChange={(e) => e.target.value && irPara({ p: null, de: e.target.value, ate })}
          />
        </label>
        <label className="gd-filtro-data">
          <span>Até</span>
          <input
            type="date"
            value={ate}
            max={hoje}
            onChange={(e) => e.target.value && irPara({ p: null, de, ate: e.target.value })}
          />
        </label>
        {(produto || vendedor) && (
          <button
            type="button"
            className="gd-limpar"
            onClick={() => irPara({ produto: null, vendedor: null })}
          >
            <Icone nome="x" tamanho={13} />{' '}
            {produto ? (produtosDoFiltro.find((x) => x[0] === produto)?.[1] ?? 'produto') : ''}
            {produto && vendedor ? ' · ' : ''}
            {vendedor ? nomeBonito(vendedor) : ''}
          </button>
        )}
      </div>

      {erro ? (
        <ErroDeArea oQue="as vendas da geladeira" aoTentar={carregar} />
      ) : (
        <>
          <div className="gd-kpis">
            <Cartao
              rotulo="Unidades vendidas"
              icone="box"
              valor={v ? qtd(v.resumo.quantidade) : null}
              sub={v ? `${String(v.resumo.cupons)} cupons` : null}
            />
            <Cartao
              rotulo="Valor das canetas"
              icone="money"
              valor={v ? brl(v.resumo.valor) : null}
              sub={
                v && v.resumo.cupons
                  ? `média ${brl(v.resumo.valor / v.resumo.cupons)} por cupom`
                  : null
              }
            />
            <Cartao
              rotulo="Mais vendida"
              icone="trophy"
              valor={v ? (maisVendida?.nome ?? '—') : null}
              sub={
                maisVendida
                  ? `${qtd(maisVendida.quantidade)} un. · ${brl(maisVendida.valor)}`
                  : null
              }
            />
            <Cartao
              rotulo="Quem mais vendeu"
              icone="user"
              valor={v ? (nomeBonito(v.resumo.porVendedor[0]?.nome) ?? '—') : null}
              sub={v?.resumo.porVendedor[0] ? brl(v.resumo.porVendedor[0].valor) : null}
            />
          </div>

          {v && v.vendas.length > 0 && (
            <div className="gd-tres">
              <Ranking
                titulo="Por produto"
                itens={v.resumo.porProduto}
                aoEscolher={(nome) =>
                  irPara({ produto: produtosDoFiltro.find((x) => x[1] === nome)?.[0] ?? null })
                }
              />
              <Ranking
                titulo="Por vendedor"
                itens={v.resumo.porVendedor}
                formatar={nomeBonito}
                aoEscolher={(nome) => irPara({ vendedor: nome })}
              />
              <Ranking titulo="Por pagamento" itens={v.resumo.porPagamento} />
            </div>
          )}

          <div className="gd-bloco">
            {v && !v.vendas.length ? (
              <EstadoVazio
                icone="receipt"
                titulo="Nenhuma venda no período"
                texto="Quando uma caneta da lista for vendida, ela aparece aqui e no Telegram na hora."
              />
            ) : (
              <table className="cv-tabela gd-tabela">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Produto</th>
                    <th className="cv-dir">Qtd</th>
                    <th className="cv-dir">Valor</th>
                    <th>Vendedor</th>
                    <th>Cliente</th>
                    <th>Pagamento</th>
                    <th className="cv-dir">Cupom</th>
                  </tr>
                </thead>
                <tbody>
                  {v === null
                    ? Array.from({ length: 6 }, (_, i) => (
                        <tr key={i} className="gd-esqueleto">
                          <td colSpan={8}>
                            <span />
                          </td>
                        </tr>
                      ))
                    : v.vendas.map((x) => (
                        <Fragment key={x.chave}>
                          <tr
                            className="cv-clicavel"
                            onClick={() => setAberta(aberta === x.chave ? null : x.chave)}
                            aria-expanded={aberta === x.chave}
                          >
                            <td>
                              <strong className="gd-nome">{diaCurto(x.dia)}</strong>
                              <small className="gd-desc">{horaBrasilia(x.instante)}</small>
                            </td>
                            <td>
                              <strong>{x.nome ?? x.descricao}</strong>
                            </td>
                            <td className="cv-dir gd-num">{qtd(x.quantidade)}</td>
                            <td className="cv-dir gd-num gd-forte">{brl(x.valor)}</td>
                            <td>
                              {x.vendedor.codigo !== null && x.vendedor.codigo < 1
                                ? 'Site / integração'
                                : (nomeBonito(x.vendedor.nome) ?? '—')}
                            </td>
                            <td>
                              {nomeBonito(x.cliente.nome) ?? (
                                <span className="gd-apagado">não identificado</span>
                              )}
                            </td>
                            <td>
                              {x.pagamentoResumo ?? (
                                <span className="gd-apagado">não identificado</span>
                              )}
                            </td>
                            <td className="cv-dir gd-num">{x.cupom ?? x.idVenda}</td>
                          </tr>
                          {aberta === x.chave && (
                            <tr className="gd-detalhe">
                              <td colSpan={8}>
                                <DetalheDaVenda v={x} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DetalheDaVenda({ v }: { v: VendaDaGeladeira }) {
  const d = v.detalhe;
  const cartoes = (d?.pagamento ?? []).flatMap((f) =>
    f.cartoes.map((c) => ({ ...c, forma: f.rotulo, parcelas: f.parcelas })),
  );
  return (
    <div className="gd-detalhe-grade">
      <div>
        <h3>Cupom inteiro · {brl(v.valorCupom)}</h3>
        <ul>
          {(d?.itens ?? []).map((i, n) => (
            <li key={n} className={i.alvo ? 'gd-alvo' : undefined}>
              {qtd(i.quantidade)} × {i.produto.descricao ?? '—'}{' '}
              <span className="gd-num">{brl(i.valor)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3>Pagamento</h3>
        <ul>
          {(d?.pagamento ?? []).map((f, n) => (
            <li key={n}>
              {f.rotulo}
              {f.parcelas > 1
                ? ` · ${String(f.parcelas)}x de ${brl(f.valorDaParcela ?? f.valor / f.parcelas)}`
                : ''}{' '}
              <span className="gd-num">{brl(f.valor)}</span>
            </li>
          ))}
          {cartoes.map((c, n) => (
            <li key={`c${String(n)}`} className="gd-apagado">
              {[
                c.bandeira,
                c.final ? `final ${c.final}` : null,
                c.autorizacao ? `aut. ${c.autorizacao}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3>Cliente</h3>
        <p>
          {nomeBonito(v.cliente.nome) ?? 'Não identificado no cupom'}
          {v.cliente.codigo ? <span className="gd-apagado"> · cód. {v.cliente.codigo}</span> : null}
        </p>
        <p className="gd-apagado">
          {d?.cliente?.cpfNaNota ? 'CPF na nota' : 'Sem CPF na nota'}
          {d?.numeroCaixa ? ` · caixa ${String(d.numeroCaixa)}` : ''}
          {d?.teleEntrega ? ' · entrega' : ''}
        </p>
      </div>
    </div>
  );
}

function Ranking({
  titulo,
  itens,
  formatar,
  aoEscolher,
}: {
  titulo: string;
  itens: VendasDaGeladeira['resumo']['porProduto'];
  formatar?: (s: string) => string | null;
  aoEscolher?: (nome: string) => void;
}) {
  const max = Math.max(...itens.map((i) => i.valor), 1);
  return (
    <div className="gd-ranking">
      <h2 className="gd-h2">{titulo}</h2>
      <ol>
        {itens.slice(0, 6).map((i) => (
          <li key={i.nome}>
            {aoEscolher ? (
              <button type="button" onClick={() => aoEscolher(i.nome)}>
                {formatar?.(i.nome) ?? i.nome}
              </button>
            ) : (
              <span>{formatar?.(i.nome) ?? i.nome}</span>
            )}
            <span className="gd-num">
              {qtd(i.quantidade)} un. · {brl(i.valor)}
            </span>
            <span
              className="gd-ranking-barra"
              style={{ width: `${String((i.valor / max) * 100)}%` }}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ================================================================== *
 * HISTÓRICO
 * ================================================================== */

function AbaHistorico({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const ate = parametros.get('ate') ?? hoje;
  const de = parametros.get('de') ?? menosDias(ate, 13);
  const [h, setH] = useState<HistoricoDaGeladeira | null>(null);
  const [erro, setErro] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const carregar = useCallback(() => {
    setErro(false);
    setH(null);
    void lerHistorico(de, ate).then((r) => (r.ok ? setH(r.dados) : setErro(true)));
  }, [de, ate]);
  useEffect(carregar, [carregar]);

  // um dia por linha (a última conferência do dia vale; as anteriores aparecem como recontagem)
  const dias = useMemo(() => {
    const out: {
      dia: string;
      vale: ConferenciaDaGeladeira | null;
      antes: ConferenciaDaGeladeira[];
    }[] = [];
    for (let d = ate; d >= de; d = menosDias(d, 1)) {
      const doDia = (h?.conferencias ?? []).filter((c) => c.dia === d);
      out.push({ dia: d, vale: doDia[0] ?? null, antes: doDia.slice(1) });
    }
    return out;
  }, [h, de, ate]);
  const nomes = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of h?.conferencias ?? []) for (const k of c.contagens) m.set(k.codigoHos, k.nome);
    return m;
  }, [h]);

  return (
    <div className="gd-corpo">
      <div className="gd-filtros">
        <label className="gd-filtro-data">
          <span>De</span>
          <input
            type="date"
            value={de}
            max={ate}
            onChange={(e) => e.target.value && irPara({ de: e.target.value })}
          />
        </label>
        <label className="gd-filtro-data">
          <span>Até</span>
          <input
            type="date"
            value={ate}
            max={hoje}
            onChange={(e) => e.target.value && irPara({ ate: e.target.value })}
          />
        </label>
      </div>
      {erro ? (
        <ErroDeArea oQue="o histórico da geladeira" aoTentar={carregar} />
      ) : (
        <>
          <div className="gd-bloco">
            <h2 className="gd-h2">Conferências</h2>
            <ul className="gd-dias">
              {dias.map(({ dia, vale, antes }) => (
                <li
                  key={dia}
                  className={
                    !vale ? 'gd-dia-sem' : vale.itensDivergentes ? 'gd-dia-erro' : 'gd-dia-ok'
                  }
                >
                  <button
                    type="button"
                    className="gd-dia-linha"
                    disabled={!vale}
                    onClick={() => setAberta(aberta === dia ? null : dia)}
                    aria-expanded={aberta === dia}
                  >
                    <span className="gd-dia-data">{diaCurto(dia)}</span>
                    {h === null ? (
                      <span className="gd-apagado">…</span>
                    ) : vale ? (
                      <>
                        <span>
                          {nomeBonito(vale.por.nome) ?? vale.por.nome} às{' '}
                          {horaBrasilia(vale.fechadaEm)}
                          {antes.length ? (
                            <span className="gd-apagado"> · recontou {antes.length}×</span>
                          ) : null}
                        </span>
                        <span
                          className={`gd-chip gd-chip-${vale.itensDivergentes ? 'erro' : 'ok'}`}
                        >
                          {vale.itensDivergentes
                            ? `${String(vale.itensDivergentes)} não bateram`
                            : 'Tudo bateu'}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="gd-apagado">Sem conferência</span>
                        <span className="gd-chip gd-chip-aviso">Não conferida</span>
                      </>
                    )}
                    {vale && <Icone nome={aberta === dia ? 'chev-d' : 'chev-r'} tamanho={14} />}
                  </button>
                  {aberta === dia && vale && (
                    <div className="gd-dia-detalhe">
                      {[vale, ...antes].map((c, n) => (
                        <div key={c.id} className="gd-dia-conf">
                          <h3>
                            {n === 0 ? 'Vale' : 'Recontagem anterior'} · {horaBrasilia(c.fechadaEm)}{' '}
                            · {nomeBonito(c.por.nome) ?? c.por.nome}
                          </h3>
                          <table className="cv-tabela gd-tabela">
                            <thead>
                              <tr>
                                <th>Produto</th>
                                <th className="cv-dir">Farmácia</th>
                                <th className="cv-dir">Estoque</th>
                                <th className="cv-dir">Contado</th>
                                <th className="cv-dir">HOS</th>
                                <th>Situação</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.contagens.map((k) => (
                                <tr
                                  key={k.produtoId}
                                  className={k.diferenca ? 'gd-linha-erro' : undefined}
                                >
                                  <td>
                                    <strong className="gd-nome">{k.nome}</strong>
                                  </td>
                                  <td className="cv-dir gd-num">{qtd(k.farmacia)}</td>
                                  <td className="cv-dir gd-num">{qtd(k.estoque)}</td>
                                  <td className="cv-dir gd-num gd-forte">{qtd(k.total)}</td>
                                  <td className="cv-dir gd-num">
                                    {k.hosSaldo === null ? '—' : qtd(k.hosSaldo)}
                                  </td>
                                  <td>
                                    <span
                                      className={`gd-chip gd-chip-${k.diferenca === null ? 'cinza' : k.diferenca ? 'erro' : 'ok'}`}
                                    >
                                      {k.diferenca === null
                                        ? 'HOS sem leitura'
                                        : k.diferenca === 0
                                          ? 'Bateu'
                                          : k.diferenca > 0
                                            ? `Sobra ${qtd(k.diferenca)}`
                                            : `Falta ${qtd(-k.diferenca)}`}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {c.observacao && <p className="gd-obs-lida">{c.observacao}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {h && h.mexidas.length > 0 && (
            <div className="gd-bloco">
              <h2 className="gd-h2">Mexidas que não foram venda</h2>
              <ListaDeMexidas mexidas={h.mexidas} nomes={nomes} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================================================================== *
 * PRODUTOS
 * ================================================================== */

function AbaProdutos({ sessao }: { sessao: Sessao }) {
  const [lista, setLista] = useState<readonly ProdutoDaGeladeira[] | null>(null);
  const [erro, setErro] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [termo, setTermo] = useState('');
  const [achados, setAchados] = useState<readonly ProdutoDoCatalogo[] | null>(null);
  const [nomeNovo, setNomeNovo] = useState<Record<string, string>>({});
  const [editando, setEditando] = useState<Record<string, string>>({});
  const carregar = useCallback(() => {
    setErro(false);
    void lerProdutos().then((r) => (r.ok ? setLista(r.dados.produtos) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    if (termo.trim().length < 2) {
      setAchados(null);
      return;
    }
    const t = setTimeout(() => {
      void buscarNoCatalogo(termo.trim()).then((r) => setAchados(r.ok ? r.dados.produtos : []));
    }, 250);
    return () => clearTimeout(t);
  }, [termo]);

  const falhou = (m: string) => {
    setAviso(m);
    setTimeout(() => setAviso(null), 5000);
  };
  const incluir = async (p: ProdutoDoCatalogo) => {
    const nome = (nomeNovo[p.codigoHos] ?? sugestaoDeNome(p.descricao)).trim();
    const r = await incluirProduto(sessao, p.codigoHos, nome);
    if (!r.ok)
      return falhou(r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não deu para incluir agora.');
    setTermo('');
    carregar();
  };
  const mudar = async (
    p: ProdutoDaGeladeira,
    m: { nome?: string; ordem?: number; ativo?: boolean },
  ) => {
    const r = await alterarProduto(sessao, p.id, m);
    if (!r.ok)
      return falhou(r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não deu para salvar agora.');
    carregar();
  };
  const ativos = (lista ?? []).filter((p) => p.ativo);
  const mover = (p: ProdutoDaGeladeira, d: -1 | 1) => {
    const i = ativos.findIndex((x) => x.id === p.id);
    const outro = ativos[i + d];
    if (!outro) return;
    void (async () => {
      await alterarProduto(sessao, p.id, { ordem: outro.ordem });
      await alterarProduto(sessao, outro.id, {
        ordem: p.ordem === outro.ordem ? p.ordem + d * -1 : p.ordem,
      });
      carregar();
    })();
  };
  const naLista = new Set((lista ?? []).filter((p) => p.ativo).map((p) => p.codigoHos));

  if (erro) return <ErroDeArea oQue="a lista da geladeira" aoTentar={carregar} />;
  return (
    <div className="gd-corpo">
      {aviso && (
        <div className="gd-aviso gd-aviso-erro" role="alert">
          <Icone nome="warn" tamanho={15} />
          <span>{aviso}</span>
        </div>
      )}
      <div className="gd-bloco">
        <h2 className="gd-h2">Pôr um produto na conferência</h2>
        <label className="gd-busca">
          <Icone nome="search" tamanho={16} />
          <input
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Nome, EAN ou código do HOS (ex.: mounjaro)"
            aria-label="Buscar no catálogo do HOS"
          />
        </label>
        {achados && (
          <ul className="gd-achados">
            {!achados.length && (
              <li className="gd-apagado">Nada no catálogo do HOS com esse nome.</li>
            )}
            {achados.map((p) => (
              <li key={p.codigoHos}>
                <div>
                  <strong>{p.descricao}</strong>
                  <small>
                    cód. HOS {p.codigoHos}
                    {p.fabricante ? ` · ${p.fabricante}` : ''}
                    {p.estoque !== null ? ` · estoque ${qtd(Number(p.estoque))}` : ''}
                  </small>
                </div>
                {naLista.has(p.codigoHos) ? (
                  <span className="gd-chip gd-chip-ok">Já está na lista</span>
                ) : (
                  <div className="gd-incluir">
                    <input
                      aria-label={`Nome curto para ${p.descricao}`}
                      value={nomeNovo[p.codigoHos] ?? sugestaoDeNome(p.descricao)}
                      maxLength={60}
                      onChange={(e) => setNomeNovo({ ...nomeNovo, [p.codigoHos]: e.target.value })}
                    />
                    <button
                      type="button"
                      className="gd-btn gd-btn-curto"
                      onClick={() => void incluir(p)}
                    >
                      <Icone nome="plus" tamanho={14} traco={2} /> Pôr na lista
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="gd-bloco">
        <h2 className="gd-h2">
          Na conferência ({ativos.length}){' '}
          <small className="gd-apagado">— na ordem em que o Iggor conta</small>
        </h2>
        {lista === null ? (
          <div className="gd-carregando" />
        ) : (
          <ul className="gd-produtos">
            {lista.map((p) => (
              <li key={p.id} className={p.ativo ? undefined : 'gd-produto-fora'}>
                {p.ativo && (
                  <span className="gd-ordem">
                    <button
                      type="button"
                      className="gd-btn-icone"
                      onClick={() => mover(p, -1)}
                      aria-label={`Subir ${p.nome}`}
                      disabled={ativos[0]?.id === p.id}
                    >
                      <Icone nome="up-t" tamanho={13} />
                    </button>
                    <button
                      type="button"
                      className="gd-btn-icone"
                      onClick={() => mover(p, 1)}
                      aria-label={`Descer ${p.nome}`}
                      disabled={ativos.at(-1)?.id === p.id}
                    >
                      <Icone nome="chev-d" tamanho={13} />
                    </button>
                  </span>
                )}
                <div className="gd-produto-nome">
                  {editando[p.id] !== undefined ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const nome = editando[p.id] ?? p.nome;
                        setEditando(({ [p.id]: _x, ...resto }) => resto);
                        if (nome.trim() && nome.trim() !== p.nome)
                          void mudar(p, { nome: nome.trim() });
                      }}
                    >
                      <input
                        autoFocus
                        value={editando[p.id]}
                        maxLength={60}
                        onChange={(e) => setEditando({ ...editando, [p.id]: e.target.value })}
                        aria-label="Nome curto"
                      />
                      <button type="submit" className="gd-btn gd-btn-curto">
                        Salvar
                      </button>
                    </form>
                  ) : (
                    <>
                      <strong>{p.nome}</strong>
                      <small>
                        {p.descricaoHos ?? '—'} · cód. HOS {p.codigoHos}
                      </small>
                    </>
                  )}
                </div>
                <div className="gd-produto-acoes">
                  {p.ativo && editando[p.id] === undefined && (
                    <button
                      type="button"
                      className="gd-btn-icone"
                      onClick={() => setEditando({ ...editando, [p.id]: p.nome })}
                      aria-label={`Renomear ${p.nome}`}
                    >
                      <Icone nome="edit" tamanho={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    className={p.ativo ? 'gd-link-perigo' : 'gd-link'}
                    onClick={() => void mudar(p, { ativo: !p.ativo })}
                  >
                    {p.ativo ? 'Tirar da lista' : 'Pôr de volta'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="gd-nota">
          Tirar da lista não apaga nada: as conferências e as vendas antigas continuam no histórico.
        </p>
      </div>
    </div>
  );
}

/** "MOUNJARO 5MG INJ 0,5ML +4CAN" → "Mounjaro 5mg" (o admin pode trocar antes de pôr). */
export function sugestaoDeNome(descricao: string): string {
  const palavras = descricao.trim().split(/\s+/);
  const nome = palavras[0] ?? descricao;
  const dose = palavras.slice(1).find((w) => /^\d+([.,]\d+)?(\/\d+([.,]\d+)?)?MG/i.test(w));
  const bonito = nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase();
  return (dose ? `${bonito} ${dose.replace(/MG.*$/i, 'mg')}` : bonito).slice(0, 60);
}
